import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Trash2, ShoppingCart, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../components/CurrencySelector';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WishlistPage = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { format } = useCurrency();
  const { isAuthenticated, token } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const [wishlist, setWishlist] = useState({ products: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWishlist = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/wishlist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist(response.data);
      } catch (error) {
        console.error('Error fetching wishlist:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWishlist();
  }, [isAuthenticated, token]);

  const handleRemove = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist({
        ...wishlist,
        products: wishlist.products.filter(p => p.id !== productId)
      });
      toast.success(language === 'fr' ? 'Retiré des favoris' : 'Removed from wishlist');
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleAddToCart = async (productId) => {
    try {
      await addToCart(productId);
      toast.success(language === 'fr' ? 'Ajouté au panier' : 'Added to cart');
    } catch (error) {
      toast.error(error.message || 'Error');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="wishlist-page">
        <div className="text-center">
          <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('wishlist')}</h2>
          <p className="text-muted-foreground mb-6">
            {language === 'fr' ? 'Connectez-vous pour voir vos favoris' : 'Login to view your wishlist'}
          </p>
          <Button onClick={() => navigate('/login')} className="btn-primary">
            {t('login')}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="aspect-square rounded-xl" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!wishlist.products || wishlist.products.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="wishlist-page-empty">
        <div className="text-center">
          <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">
            {language === 'fr' ? 'Aucun favori' : 'No favorites yet'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {language === 'fr' ? 'Découvrez nos produits' : 'Discover our products'}
          </p>
          <Button onClick={() => navigate('/shop')} className="btn-primary">
            {t('shopNow')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="wishlist-page">
      <div className="container-main py-8">
        <h1 className="text-3xl font-bold mb-8">{t('wishlist')}</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {wishlist.products.map((product) => (
            <div key={product.id} className="bg-white rounded-xl border border-border/40 overflow-hidden group">
              <div 
                className="aspect-square relative cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <img
                  src={product.image || 'https://via.placeholder.com/300'}
                  alt={getLocalizedName(product)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute top-3 right-3 bg-white shadow-lg text-red-500"
                  onClick={(e) => { e.stopPropagation(); handleRemove(product.id); }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="p-4">
                <p className="text-xs text-muted-foreground mb-1">{product.vendor_name}</p>
                <h3 
                  className="font-semibold line-clamp-2 cursor-pointer hover:text-[#0F4C5C]"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  {getLocalizedName(product)}
                </h3>
                <p className="text-lg font-bold text-[#0F4C5C] mt-2">
                  €{product.price?.toFixed(2)}
                </p>
                
                <Button
                  className="w-full mt-4 btn-primary"
                  onClick={() => handleAddToCart(product.id)}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('addToCart')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
